import { Box, Card, CardBody, Heading, Icon, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { IconType } from "react-icons";
import {
  FaBell,
  FaBuilding,
  FaChartLine,
  FaFileContract,
  FaFileImport,
  FaFileSignature,
  FaListUl,
} from "react-icons/fa";
import { useSub } from "../../../common/subscription/useSub";
import { SettingsTab, useQueryParams } from "../../../common/useQueryParams";
import { ContractTemplates } from "../../contracts/settings/ContractTemplates/ContractTemplates";
import { CustomContractFields } from "../../contracts/settings/CustomContractFields/CustomContractFields";
import { AssessmentTemplates } from "../../settings/AssessmentTemplates";
import { DocumentTemplates } from "../../settings/DocumentTemplates";
import { InternalParties } from "../../settings/InternalParties";
import { NotificationSettings } from "../../settings/Notifications/NotificationSettings";
import { StandardTerms } from "../../settings/StandardTerms";

interface SettingsTabDefinition {
  key: SettingsTab;
  label: string;
  icon: IconType;
  isDisabled?: boolean;
  panel: ReactNode;
}

const PanelCard = ({ children }: { children: ReactNode }) => (
  <Card variant="outline" rounded="xl" bg="smBackground" borderColor="smBorder" boxShadow="sm">
    <CardBody overflowX="auto">{children}</CardBody>
  </Card>
);

export const SettingsPage = () => {
  const { t } = useTranslation();
  const sub = useSub();
  const params = useQueryParams();

  const canManageContractSettings = sub.isOrgAdmin && sub.hasContractAccess;

  const tabs: SettingsTabDefinition[] = [
    { key: "notifications", label: t("Notification Settings"), icon: FaBell, panel: <NotificationSettings /> },
    {
      key: "document-templates",
      label: t("Document Templates"),
      icon: FaFileImport,
      isDisabled: !sub.hasDocumentTemplateAccess,
      panel: <DocumentTemplates />,
    },
    { key: "internal-parties", label: t("Internal Parties"), icon: FaBuilding, panel: <InternalParties /> },
    { key: "assessment-templates", label: t("Assessment Templates"), icon: FaChartLine, panel: <AssessmentTemplates /> },
    { key: "standard-terms", label: t("Standard terms"), icon: FaFileContract, panel: <StandardTerms /> },
    ...(canManageContractSettings
      ? [
          {
            key: "contract-types" as const,
            label: t("Contract Types"),
            icon: FaFileSignature,
            panel: <ContractTemplates />,
          },
          {
            key: "custom-contract-fields" as const,
            label: t("Custom Contract Fields"),
            icon: FaListUl,
            panel: <CustomContractFields />,
          },
        ]
      : []),
  ];

  const selectedTab = params.getSettingsTab();
  const tabIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.key === selectedTab)
  );

  return (
    <Box>
      <Heading as="h1" size="lg" color="smText" mb="6">
        {t("Settings")}
      </Heading>
      <Tabs
        colorScheme="teal"
        index={tabIndex}
        onChange={(index) => params.setSelectedSettingsTab(tabs[index].key)}
        isLazy
      >
        <TabList
          overflowX="auto"
          overflowY="hidden"
          sx={{
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            "& > button": { flexShrink: 0, whiteSpace: "nowrap" },
          }}
        >
          {tabs.map((tab) => (
            <Tab key={tab.key} isDisabled={tab.isDisabled}>
              <Icon as={tab.icon} mr="2" />
              {tab.label}
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          {tabs.map((tab) => (
            <TabPanel key={tab.key} px="0" pt="5">
              <PanelCard>{tab.panel}</PanelCard>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};
